import { useState, useEffect, useCallback } from "react";
import type { CurrencyCode } from "@/hooks/useCurrency";

export interface CartItem {
  familyName: string;
  code: string;
  spec: string;
  price: number;
  quantity: number;
  isGift?: boolean;
}

const CART_KEY = "peptide_cart";
export const SHIPPING_COST_USD = 55;
export const SHIPPING_COST_AUD = 79.93;
export const FREE_SHIPPING_THRESHOLD_USD = 1000;
export const FREE_SHIPPING_THRESHOLD_AUD = 1200;

export function getShippingCost(currency: CurrencyCode): number {
  return currency === 'AUD' ? SHIPPING_COST_AUD : SHIPPING_COST_USD;
}

export function getFreeShippingThreshold(currency: CurrencyCode): number {
  return currency === 'AUD' ? FREE_SHIPPING_THRESHOLD_AUD : FREE_SHIPPING_THRESHOLD_USD;
}

function calcShipping(subtotal: number, currency: CurrencyCode): number {
  if (subtotal >= getFreeShippingThreshold(currency)) return 0;
  return getShippingCost(currency);
}

function normalizeCartItem(item: CartItem): CartItem {
  const code =
    item.code === "GTT600"
      ? "ATT600"
      : item.code === "GTT1500"
        ? "ATT1500"
        : item.code;

  return {
    ...item,
    code,
    isGift: Boolean(item.isGift),
  };
}

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    const parsed = raw ? (JSON.parse(raw) as CartItem[]) : [];
    return parsed.map(normalizeCartItem);
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
}

export function useCart(currency: CurrencyCode, allowStaffFeatures = false) {
  const [items, setItems] = useState<CartItem[]>(() => {
    const loaded = loadCart();
    return allowStaffFeatures ? loaded : loaded.filter((item) => !item.isGift);
  });
  const [discountRate, setDiscountRateState] = useState(0);

  useEffect(() => {
    saveCart(items);
  }, [items]);

  const addItem = useCallback((item: Omit<CartItem, "quantity" | "isGift">) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.code === item.code && !i.isGift);
      if (existing) {
        return prev.map((i) =>
          i.code === item.code && !i.isGift ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...item, quantity: 1, isGift: false }];
    });
  }, []);

  const addGiftItem = useCallback((item: Omit<CartItem, "quantity" | "isGift" | "price">) => {
    if (!allowStaffFeatures) return;
    setItems((prev) => {
      const existing = prev.find((i) => i.code === item.code && i.isGift);
      if (existing) {
        return prev.map((i) =>
          i.code === item.code && i.isGift ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...item, price: 0, quantity: 1, isGift: true }];
    });
  }, [allowStaffFeatures]);

  const removeItem = useCallback((code: string) => {
    setItems((prev) => prev.filter((i) => i.code !== code));
  }, []);

  const updateQuantity = useCallback((code: string, quantity: number, isGift?: boolean) => {
    const giftFlag = Boolean(isGift);

    if (quantity <= 0) {
      setItems((prev) =>
        prev.filter((i) => !(i.code === code && Boolean(i.isGift) === giftFlag))
      );
      return;
    }

    setItems((prev) =>
      prev.map((i) =>
        i.code === code && Boolean(i.isGift) === giftFlag ? { ...i, quantity } : i
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const effectiveDiscountRate = allowStaffFeatures ? discountRate : 0;
  const discount = subtotal * effectiveDiscountRate;
  const discountedSubtotal = subtotal - discount;
  const shipping = calcShipping(subtotal, currency);
  const totalPrice = discountedSubtotal + shipping;

  const setDiscountRate = useCallback((rate: number) => {
    setDiscountRateState(allowStaffFeatures ? rate : 0);
  }, [allowStaffFeatures]);

  const getOrderText = useCallback(() => {
    const shippingCost = getShippingCost(currency);
    const freeThreshold = getFreeShippingThreshold(currency);
    const symbol = currency === 'AUD' ? 'A$' : '$';
    const lines = items.map(
      (i) => `${i.familyName} | ${i.code} | ${i.spec} | ${i.isGift ? "FREE GIFT" : `${symbol}${i.price}`} × ${i.quantity}`
    );
    lines.push(`\nSubtotal: ${symbol}${subtotal.toFixed(2)}`);
    lines.push(`Shipping: ${shipping === 0 ? `FREE (orders over ${symbol}${freeThreshold})` : `${symbol}${shipping.toFixed(2)}`}`);
    lines.push(`Total: ${symbol}${totalPrice.toFixed(2)}`);
    return lines.join("\n");
  }, [items, subtotal, shipping, totalPrice, currency]);

  return {
    items,
    addItem,
    addGiftItem,
    removeItem,
    updateQuantity,
    clearCart,
    totalCount,
    subtotal,
    discount,
    discountRate: effectiveDiscountRate,
    setDiscountRate,
    discountedSubtotal,
    shipping,
    totalPrice,
    getOrderText,
  };
}